/**
 * Configuration constants for the AdminLogin component
 * These values are used throughout the component for API communication,
 * authentication, and other configuration needs.
 */

/**
 * Safely access environment variables
 * @param {string} key - The environment variable key
 * @param {*} defaultValue - Default value if not defined
 * @returns {*} The environment value or default
 */
const getEnvVar = (key, defaultValue) => {
  // Check if process and process.env exist before accessing
  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env[key] !== undefined
  ) {
    return process.env[key];
  }
  return defaultValue;
};

/**
 * Determine the current environment
 * @type {string} 'development', 'staging', or 'production'
 */
const ENV = getEnvVar("NODE_ENV", "development");

/**
 * Feature flag to control sign-up functionality visibility
 * Set to false to hide sign-up link in production, true for testing
 * @type {boolean}
 */
export const ENABLE_SIGNUP = ENV !== "production" ? true : false;

/**
 * API URL configurations for different environments
 * @type {Object.<string, string>}
 */
const API_URLS = {
  development:
    "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod/pending-join-requests",
  staging:
    "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod/pending-join-requests",
  production:
    "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod/pending-join-requests",
};

/**
 * Base API URL for the application
 * @type {string}
 */
export const API_URL = API_URLS[ENV] || API_URLS.development;

/**
 * Alias for API_URL to maintain backward compatibility
 * @type {string}
 */
export const API_BASE_URL = API_URL;

/**
 * Cognito configuration for different environments
 * @type {Object.<string, Object>}
 */
const COGNITO_CONFIGS = {
  development: {
    userPoolId: getEnvVar(
      "REACT_APP_COGNITO_USER_POOL_ID",
      "us-east-1_fc1omeTGh"
    ),
    clientId: getEnvVar(
      "REACT_APP_COGNITO_CLIENT_ID",
      "54fiucjkk95o6gna3l5l3dnjuo"
    ),
    region: getEnvVar("REACT_APP_COGNITO_REGION", "us-east-1"),
  },
  staging: {
    userPoolId: getEnvVar(
      "REACT_APP_COGNITO_USER_POOL_ID",
      "us-east-1_fc1omeTGh"
    ),
    clientId: getEnvVar(
      "REACT_APP_COGNITO_CLIENT_ID",
      "54fiucjkk95o6gna3l5l3dnjuo"
    ),
    region: getEnvVar("REACT_APP_COGNITO_REGION", "us-east-1"),
  },
  production: {
    userPoolId: getEnvVar(
      "REACT_APP_COGNITO_USER_POOL_ID",
      "us-east-1_fc1omeTGh"
    ),
    clientId: getEnvVar(
      "REACT_APP_COGNITO_CLIENT_ID",
      "54fiucjkk95o6gna3l5l3dnjuo"
    ),
    region: getEnvVar("REACT_APP_COGNITO_REGION", "us-east-1"),
  },
};

/**
 * Current Cognito configuration based on environment
 * @type {Object}
 */
export const COGNITO_CONFIG = COGNITO_CONFIGS[ENV];

/**
 * Helper function to check if Cognito is configured
 * @type {boolean}
 */
export const isCognitoConfigured =
  COGNITO_CONFIG.userPoolId && COGNITO_CONFIG.clientId;

/**
 * Default tab to display when the user logs in
 * @type {string}
 */
export const DEFAULT_ACTIVE_TAB = "pendingRequests";

/**
 * Default app to display within the apps tab
 * @type {string}
 */
export const DEFAULT_ACTIVE_APP = "userManager";

/**
 * Default number of days ahead to show for events
 * @type {number}
 */
export const DEFAULT_DAYS_AHEAD = 14;

/**
 * Default data loading state
 * @type {Object}
 */
export const DEFAULT_LOADING_STATE = {
  isLoading: false,
  error: null,
  data: null,
};
